import { useCallback, useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { type AdminUser, listAdminUsers } from '../../api/admin'
import {
  addProjectMember,
  getProjectAccess,
  type ProjectAccessEntry,
  removeProjectMember,
} from '../../api/project-access'
import { OrgPicker, OrgProvider, useOrgContext } from '../../components/admin/OrgPicker'
import { useDialog } from '../../components/dialogs/useDialog'
import { useAuth } from '../../hooks/useAuth'
import { isGlobalAdmin } from '../../utils/rbac'

function ProjectAccessPageInner() {
  const dialog = useDialog()
  const { user } = useAuth()
  const { selectedOrgId, isHqSuperadmin } = useOrgContext()
  const [projects, setProjects] = useState<ProjectAccessEntry[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [allUsers, setAllUsers] = useState<AdminUser[]>([])
  const [expandedProjectId, setExpandedProjectId] = useState<number | null>(null)
  const [addingProjectId, setAddingProjectId] = useState<number | null>(null)
  const [selectedUserId, setSelectedUserId] = useState<number | ''>('')
  const [newMemberRole, setNewMemberRole] = useState<'manager' | 'viewer'>('viewer')
  const [removingKey, setRemovingKey] = useState<string | null>(null)

  const globalAdmin = isGlobalAdmin(user?.role)

  const loadProjects = useCallback(async () => {
    try {
      const data = await getProjectAccess(isHqSuperadmin ? (selectedOrgId ?? undefined) : undefined)
      setProjects(data)
    } catch {
      toast.error('Failed to load project access')
    } finally {
      setIsLoading(false)
    }
  }, [isHqSuperadmin, selectedOrgId])

  useEffect(() => {
    void loadProjects()
  }, [loadProjects])

  useEffect(() => {
    if (!globalAdmin) return
    listAdminUsers(isHqSuperadmin ? (selectedOrgId ?? undefined) : undefined)
      .then(setAllUsers)
      .catch(() => setAllUsers([]))
  }, [globalAdmin, isHqSuperadmin, selectedOrgId])

  // Deep-link: ?project=<id>
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const projectId = params.get('project')
    if (projectId) {
      const id = Number(projectId)
      if (Number.isSafeInteger(id)) setExpandedProjectId(id)
    }
  }, [])

  const handleAdd = async (projectId: number) => {
    if (selectedUserId === '') {
      toast.error('Select a user')
      return
    }
    if (newMemberRole === 'manager' && !globalAdmin) {
      toast.error('Only admins can assign managers')
      return
    }

    setAddingProjectId(projectId)
    try {
      await addProjectMember(projectId, { userId: selectedUserId, projectRole: newMemberRole })
      toast.success('Member added')
      setSelectedUserId('')
      setNewMemberRole('viewer')
      await loadProjects()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to add member')
    } finally {
      setAddingProjectId(null)
    }
  }

  const handleRemove = async (projectId: number, userId: number) => {
    const confirmed = await dialog.confirm({
      title: 'Remove member?',
      description: 'The user will lose access to this project.',
      confirmLabel: 'Remove',
      tone: 'danger',
    })
    if (!confirmed) return

    setRemovingKey(`${projectId}-${userId}`)
    try {
      await removeProjectMember(projectId, userId)
      toast.success('Member removed')
      await loadProjects()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to remove member')
    } finally {
      setRemovingKey(null)
    }
  }

  if (isHqSuperadmin && selectedOrgId === null && !isLoading) {
    return (
      <div className="max-w-7xl mx-auto space-y-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-harbor-text">Project Access</h1>
            <p className="text-harbor-text-secondary mt-1">
              Select an organization to view project access.
            </p>
          </div>
          <OrgPicker />
        </div>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-emerald-500" />
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-harbor-text">Project Access</h1>
          <p className="text-harbor-text-secondary mt-1">Manage members across all projects.</p>
        </div>
        <OrgPicker />
      </div>

      {projects.length === 0 ? (
        <div className="rounded-xl border border-harbor-border bg-harbor-surface/40 p-8 text-center">
          <p className="text-harbor-text-secondary">No projects in this organization.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {projects.map((project) => (
            <div
              key={project.projectId}
              className="rounded-xl border border-harbor-border bg-harbor-surface/40 overflow-hidden"
            >
              <button
                type="button"
                onClick={() =>
                  setExpandedProjectId((prev) =>
                    prev === project.projectId ? null : project.projectId
                  )
                }
                className="w-full flex items-center justify-between px-6 py-4 hover:bg-harbor-surface-raised/30 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className="text-harbor-text-muted text-xs">
                    {expandedProjectId === project.projectId ? '▼' : '▶'}
                  </span>
                  <h2 className="text-lg font-semibold text-harbor-text text-left">
                    {project.name}
                  </h2>
                  <span className="text-xs text-harbor-text-muted">
                    {project.members.length} member{project.members.length !== 1 ? 's' : ''}
                  </span>
                </div>
              </button>

              {expandedProjectId === project.projectId && (
                <div className="border-t border-harbor-border">
                  {project.members.length === 0 ? (
                    <p className="px-6 py-4 text-sm text-harbor-text-muted">No members yet.</p>
                  ) : (
                    <div className="divide-y divide-harbor-border">
                      {project.members.map((member) => (
                        <div
                          key={member.userId}
                          className="flex items-center justify-between gap-4 px-6 py-3 hover:bg-harbor-surface-raised/20"
                        >
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-harbor-text truncate">
                              {member.username}
                            </p>
                            <p className="text-xs text-harbor-text-secondary truncate">
                              {member.email}
                            </p>
                          </div>
                          <div className="flex items-center gap-3 flex-shrink-0">
                            <span className="text-xs px-2 py-1 rounded-md bg-harbor-surface-raised text-harbor-text-secondary capitalize">
                              {member.projectRole}
                            </span>
                            {globalAdmin && (
                              <button
                                type="button"
                                disabled={removingKey === `${project.projectId}-${member.userId}`}
                                onClick={() => void handleRemove(project.projectId, member.userId)}
                                className="text-xs text-red-400 hover:text-red-300 disabled:opacity-50"
                              >
                                {removingKey === `${project.projectId}-${member.userId}`
                                  ? 'Removing…'
                                  : 'Remove'}
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {globalAdmin && (
                    <div className="px-6 py-4 border-t border-harbor-border bg-harbor-surface/20">
                      <div className="flex flex-wrap items-end gap-3">
                        <div>
                          <label
                            htmlFor={`member-user-${project.projectId}`}
                            className="block text-xs font-medium text-harbor-text-secondary mb-1"
                          >
                            User
                          </label>
                          <select
                            id={`member-user-${project.projectId}`}
                            value={selectedUserId}
                            onChange={(e) =>
                              setSelectedUserId(e.target.value === '' ? '' : Number(e.target.value))
                            }
                            className="px-3 py-2 bg-harbor-surface-raised border border-harbor-border rounded-lg text-harbor-text text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                          >
                            <option value="">Select user…</option>
                            {allUsers
                              .filter(
                                (u) => u.isActive && !project.members.some((m) => m.userId === u.id)
                              )
                              .map((u) => (
                                <option key={u.id} value={u.id}>
                                  {u.username} ({u.email})
                                </option>
                              ))}
                          </select>
                        </div>
                        <div>
                          <label
                            htmlFor={`member-role-${project.projectId}`}
                            className="block text-xs font-medium text-harbor-text-secondary mb-1"
                          >
                            Role
                          </label>
                          <select
                            id={`member-role-${project.projectId}`}
                            value={newMemberRole}
                            onChange={(e) =>
                              setNewMemberRole(e.target.value as 'manager' | 'viewer')
                            }
                            className="px-3 py-2 bg-harbor-surface-raised border border-harbor-border rounded-lg text-harbor-text text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                          >
                            <option value="viewer">Viewer</option>
                            <option value="manager">Manager</option>
                          </select>
                        </div>
                        <button
                          type="button"
                          disabled={addingProjectId === project.projectId}
                          onClick={() => void handleAdd(project.projectId)}
                          className="px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 disabled:opacity-50 transition-colors"
                        >
                          {addingProjectId === project.projectId ? 'Adding…' : 'Add member'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default function ProjectAccessPage() {
  return (
    <OrgProvider>
      <ProjectAccessPageInner />
    </OrgProvider>
  )
}
